import { useTranslation } from 'react-i18next';

import { groupBySection, useNegotiations, type NegotiationSection } from '@entities/negotiation';
import { NegotiationListCard } from '@features/negotiation';
import { ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import { Button, ButtonLink, Skeleton, StateBlock } from '@shared/ui';

import styles from './NegotiationsPage.module.css';

const SECTIONS: NegotiationSection[] = ['active', 'completed', 'closed'];

/**
 * N-02 «Переговоры» — список переписок (FE-172, MVP_PRD §22.1).
 *
 * Три раздела и один счётчик. Счётчик «2 из 4» приходит с сервера: размер
 * четвёрки и предел переписок — данные, а не литералы интерфейса
 * (DESIGN_SYSTEM §8).
 *
 * Экран **вне воронки заказа**: переписка переживает удаление запроса — письма
 * уже отправлены (§23.10). Поэтому трекера семи шагов здесь нет.
 *
 * Пустое состояние объясняет, **как начать**, а не констатирует пустоту:
 * переговоры начинаются из четвёрки, и ссылка ведёт туда.
 */
export function NegotiationsPage() {
  const { t } = useTranslation();
  const query = useNegotiations();

  useAppShell({ stage: null, paid: false, karl: null });

  if (query.isPending) {
    return (
      <div className={styles.page} aria-hidden="true">
        <Skeleton height="2.5rem" width="40%" />
        <Skeleton height="10rem" />
        <Skeleton height="10rem" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className={styles.page}>
        <StateBlock
          tone="error"
          title={t('negotiation.list.errorTitle')}
          description={t('negotiation.list.errorText')}
          action={
            <Button
              variant="secondary"
              onClick={() => {
                void query.refetch();
              }}
            >
              {t('error.retry')}
            </Button>
          }
        />
      </div>
    );
  }

  const groups = groupBySection(query.data.items);

  if (query.data.items.length === 0) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>{t('negotiation.list.title')}</h1>
        <StateBlock
          title={t('negotiation.list.emptyTitle')}
          description={t('negotiation.list.emptyText')}
          action={
            <ButtonLink to={ROUTES.app} variant="secondary">
              {t('negotiation.list.emptyAction')}
            </ButtonLink>
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t('negotiation.list.title')}</h1>
        <p className={styles.counter}>
          {t('negotiation.list.counter', {
            count: query.data.active_count,
            max: query.data.max_active,
          })}
        </p>
      </header>

      {SECTIONS.map((section) =>
        groups[section].length === 0 ? null : (
          <section key={section} className={styles.section}>
            <h2 className={styles.sectionTitle}>{t(`negotiation.list.section.${section}`)}</h2>
            <ul className={styles.items}>
              {groups[section].map((item) => (
                <li key={item.negotiation_id}>
                  <NegotiationListCard item={item} />
                </li>
              ))}
            </ul>
          </section>
        ),
      )}
    </div>
  );
}
